// Story 7.1 — Live A landing page. Replaces the Story 1.3 smoke card that
// previously lived inline in App.tsx as <HomePage />.
//
// Design brief per memory/project_feed_and_launch_strategy.md (party-mode
// session 2026-07-01): marketplace-not-SoMe posture, Instagram-belonging /
// eToro-substance register, forest-dark flat background, antique-gold
// accents, no motion, no marketing sections stacked below the fold.
//
// Story 7.6 — signed-in users hitting `/` bounce to `/feed`, the post-login
// home (rendered inside <AppShell>). The landing scene below only ever
// renders for signed-out visitors.
//
// LEGAL CHROME: this is a CHROMELESS route (App.tsx) — it owns its own
// full-screen layout and never renders inside <AppShell>, so it never
// inherited the shell's <SiteFooter>. That left the ONE page an
// unauthenticated visitor actually lands on with no trader identification
// (legal name / CVR / address / contact) and no reachable link to the terms
// they are about to accept in the create-account form six inches above.
// Danish e-commerce trader-identification duty bites hardest exactly here,
// so the footer is mounted directly by this page.

import { useEffect } from "react";
import { Navigate, useNavigate } from "react-router-dom";

import { SiteFooter } from "@greenhold/components";

import { useAccountMe } from "../features/admin/useAccountMe";
import { AuthCard } from "../features/auth/AuthCard";

const POST_LOGIN_REDIRECT = "/feed";

export function LandingPage() {
  const navigate = useNavigate();
  const session = useAccountMe();

  useEffect(() => {
    if (session.data?.data.user_id) {
      navigate(POST_LOGIN_REDIRECT, { replace: true });
    }
  }, [session.data, navigate]);

  // Spinner ONLY on the first-ever resolution. For a signed-out visitor
  // the query rests in error state (401) with no data, and any window
  // blur→focus (e.g. opening the ToS link in a new tab) triggers a focus
  // refetch that flips it back to `pending`. Gating on bare `isPending`
  // made that refetch unmount <AuthCard>, wiping the active tab and every
  // form field mid-signup.
  if (session.isPending && session.errorUpdateCount === 0) {
    return (
      <main
        className="min-h-screen flex items-center justify-center bg-brand-forest-900 text-brand-paper-100"
        aria-busy="true"
      >
        <div
          className="h-10 w-10 rounded-full border-2 border-brand-gold-300 border-t-transparent animate-spin"
          aria-label="Loading"
        />
      </main>
    );
  }

  if (session.data?.data.user_id) {
    return <Navigate to={POST_LOGIN_REDIRECT} replace />;
  }

  // The page is a min-h-screen flex COLUMN: <main> grows, <SiteFooter> is
  // the last row. Previously <main> was itself min-h-screen, so appending a
  // footer would have parked the trader identification a full viewport
  // below the fold. The "Creators. Issues. Ownership." triplet is no longer
  // a <footer> element — SiteFooter is the page's one contentinfo landmark
  // — but it keeps its pinned-to-the-bottom position via `mt-auto`.
  return (
    <div className="min-h-screen bg-brand-forest-900 text-brand-paper-100 flex flex-col">
      <main className="flex-1 flex flex-col items-center px-6 pt-24 pb-12">
        <div className="w-full max-w-md flex flex-col items-start gap-6">
          <h1
            className="font-heading text-4xl tracking-wide text-brand-gold-300 m-0"
            data-testid="landing-wordmark"
          >
            Greenhold
          </h1>
          <p className="font-body text-brand-paper-100/80 leading-relaxed m-0">
            A home for esport heritage. We&apos;re assembling the first cohort
            of creators.
          </p>
          <AuthCard />
        </div>
        <p className="mt-auto mb-0 pt-12 text-center font-body text-sm text-brand-paper-100/60">
          Creators. Issues. Ownership.
        </p>
      </main>
      {/* mt-0: the landing ground IS brand-forest-900, the same colour as
          the footer, so the shared component's default mt-16 would only add
          an invisible dark gap and push the gold hairline away from the
          content edge it is supposed to separate. */}
      <SiteFooter className="mt-0" />
    </div>
  );
}
